"use client";

import { useLanguage } from "../../context/LanguageContext";
import type { NavSectionId } from "../../lib/navigationTypes";
import { BulletItem } from "../BulletItem";
import { InfoCard, infoCardListClassName } from "../InfoCard";
import {
  cardHeadingClass,
  sectionHeadingClass,
  sectionIntroClass,
  sectionLabelClass
} from "../typography";

type HeadingLevel = "h1" | "h2";

type DecisionSupportSectionProps = {
  onNavigate?: (section: NavSectionId) => void;
  headingLevel?: HeadingLevel;
};

export default function DecisionSupportSection({
  onNavigate,
  headingLevel = "h2"
}: DecisionSupportSectionProps): JSX.Element {
  const { t } = useLanguage();
  const HeadingTag: HeadingLevel = headingLevel;

  return (
    <section
      id="decisions"
      className="container-page scroll-mt-28 py-16 sm:py-20"
      data-testid="decisions-section"
    >
      <div className="mb-10 space-y-3">
        <p className={sectionLabelClass}>{t.decisions.label}</p>
        <HeadingTag className={`${sectionHeadingClass} rtl:text-right`}>{t.decisions.title}</HeadingTag>
        <p className={`${sectionIntroClass} text-start rtl:text-right`}>{t.decisions.intro}</p>
      </div>

      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <InfoCard className="flex h-full flex-col">
            <div className="text-start rtl:text-right">
              <h4 className={cardHeadingClass}>{t.decisions.when.title}</h4>
            </div>
            <ul className={infoCardListClassName}>
              {t.decisions.when.points.map((point) => (
                <BulletItem key={point} text={point} />
              ))}
            </ul>
          </InfoCard>

          <InfoCard className="flex h-full flex-col">
            <div className="text-start rtl:text-right">
              <h4 className={cardHeadingClass}>{t.decisions.whatYouGet.title}</h4>
            </div>
            <ul className={infoCardListClassName}>
              {t.decisions.whatYouGet.points.map((point) => (
                <BulletItem key={point} text={point} />
              ))}
            </ul>
          </InfoCard>
        </div>

        <InfoCard>
          <div className="text-start rtl:text-right">
            <h4 className={cardHeadingClass}>{t.decisions.examples.title}</h4>
          </div>
          <ul className={infoCardListClassName}>
            {t.decisions.examples.items.map((item) => (
              <BulletItem key={item} text={item} />
            ))}
          </ul>
        </InfoCard>
      </div>

      {t.decisions.cta && (
        <div className="mt-12 flex flex-col items-center gap-4 text-center">
          <p className="text-lg text-slate-600 dark:text-slate-400">
            {t.decisions.cta.text}
          </p>
          <button
            type="button"
            onClick={() => onNavigate?.("contact")}
            className="rounded-full bg-primary-600 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-primary-500/20 transition-colors hover:bg-primary-700"
          >
            {t.decisions.cta.button}
          </button>
        </div>
      )}
    </section>
  );
}
